import { CategoryInfo, DocumentType, PageFormat } from '../types/document';

export const CATEGORIES: CategoryInfo[] = [
  {
    id: 'books',
    name: 'Books',
    description: "Novels, children's books, comics, poetry, and more",
    icon: 'BookOpen',
    color: '#8B5CF6',  // Purple
  },
  {
    id: 'business',
    name: 'Business',
    description: 'Reports, proposals, handbooks, and presentations',
    icon: 'Briefcase',
    color: '#3B82F6',  // Blue
  },
  {
    id: 'legal',
    name: 'Legal',
    description: 'Contracts, briefs, demand letters, and agreements',
    icon: 'Scale',
    color: '#EF4444',  // Red
  },
  {
    id: 'creative',
    name: 'Creative',
    description: 'Screenplays, stage plays, poetry, and zines',
    icon: 'Sparkles',
    color: '#F59E0B',  // Amber
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'Brochures, newsletters, flyers, and catalogs',
    icon: 'Megaphone',
    color: '#10B981',  // Green
  },
];

export const PAGE_FORMATS: PageFormat[] = [
  // Book sizes
  { id: 'square-8.5', name: 'Square (8.5")', shortName: '8.5x8.5', widthInches: 8.5, heightInches: 8.5, orientation: 'square', category: ['books'] },
  { id: 'picture-8x10', name: 'Picture Book (8x10")', shortName: '8x10', widthInches: 8, heightInches: 10, orientation: 'portrait', category: ['books'] },
  { id: 'picture-10x8', name: 'Picture Book Landscape (10x8")', shortName: '10x8', widthInches: 10, heightInches: 8, orientation: 'landscape', category: ['books'] },
  { id: 'pocket-5x8', name: 'Pocket (5x8")', shortName: '5x8', widthInches: 5, heightInches: 8, orientation: 'portrait', category: ['books', 'creative'] },
  { id: 'digest-5.5x8.5', name: 'Digest (5.5x8.5")', shortName: '5.5x8.5', widthInches: 5.5, heightInches: 8.5, orientation: 'portrait', category: ['books', 'creative'] },
  { id: 'trade-6x9', name: 'Trade (6x9")', shortName: '6x9', widthInches: 6, heightInches: 9, orientation: 'portrait', category: ['books', 'creative'] },
  { id: 'textbook-7x10', name: 'Textbook (7x10")', shortName: '7x10', widthInches: 7, heightInches: 10, orientation: 'portrait', category: ['books'] },
  { id: 'comic-standard', name: 'Comic Standard (6.625x10.25")', shortName: '6.625x10.25', widthInches: 6.625, heightInches: 10.25, orientation: 'portrait', category: ['books'] },
  { id: 'manga-digest', name: 'Manga Digest (5x7.5")', shortName: '5x7.5', widthInches: 5, heightInches: 7.5, orientation: 'portrait', category: ['books'] },

  // Standard paper sizes
  { id: 'letter', name: 'US Letter (8.5x11")', shortName: '8.5x11', widthInches: 8.5, heightInches: 11, orientation: 'portrait', category: ['business', 'legal', 'creative', 'marketing'] },
  { id: 'legal', name: 'US Legal (8.5x14")', shortName: '8.5x14', widthInches: 8.5, heightInches: 14, orientation: 'portrait', category: ['legal'] },
  { id: 'a4', name: 'A4 (210x297mm)', shortName: 'A4', widthInches: 8.27, heightInches: 11.69, orientation: 'portrait', category: ['business', 'legal', 'marketing'] },
  { id: 'tabloid', name: 'Tabloid (11x17")', shortName: '11x17', widthInches: 11, heightInches: 17, orientation: 'portrait', category: ['marketing'] },
  { id: 'half-letter', name: 'Half Letter (5.5x8.5")', shortName: '5.5x8.5', widthInches: 5.5, heightInches: 8.5, orientation: 'portrait', category: ['creative', 'marketing'] },

  // Presentation sizes
  { id: 'widescreen', name: 'Widescreen (16:9)', shortName: '16:9', widthInches: 13.33, heightInches: 7.5, orientation: 'landscape', category: ['business', 'marketing'] },
  { id: 'standard-4x3', name: 'Standard (4:3)', shortName: '4:3', widthInches: 10, heightInches: 7.5, orientation: 'landscape', category: ['business', 'marketing'] },

  // Social media
  { id: 'social-square', name: 'Social Square (1:1)', shortName: '1:1', widthInches: 10, heightInches: 10, orientation: 'square', category: ['marketing'] },
  { id: 'social-story', name: 'Social Story (9:16)', shortName: '9:16', widthInches: 6.75, heightInches: 12, orientation: 'portrait', category: ['marketing'] },
];

export const DOCUMENT_TYPES: DocumentType[] = [
  // Books
  {
    id: 'childrens-picture',
    name: "Children's Picture Book",
    description: 'Illustrated storybook for young readers',
    category: 'books',
    icon: 'Baby',
    defaultFormat: 'square-8.5',
    availableFormats: ['square-8.5', 'picture-8x10', 'picture-10x8'],
    features: { showImages: true, showCover: true, showChapters: false, showPageNumbers: true, showHeaders: false, showDataViz: false },
  },
  {
    id: 'childrens-chapter',
    name: "Children's Chapter Book",
    description: 'Longer stories with chapters for young readers',
    category: 'books',
    icon: 'BookText',
    defaultFormat: 'digest-5.5x8.5',
    availableFormats: ['digest-5.5x8.5', 'trade-6x9'],
    features: { showImages: true, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: false },
  },
  {
    id: 'novel',
    name: 'Novel / Fiction',
    description: 'Standard fiction novel',
    category: 'books',
    icon: 'Book',
    defaultFormat: 'trade-6x9',
    availableFormats: ['pocket-5x8', 'digest-5.5x8.5', 'trade-6x9'],
    features: { showImages: false, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: false },
  },
  {
    id: 'nonfiction',
    name: 'Non-Fiction',
    description: 'Educational or informational books',
    category: 'books',
    icon: 'GraduationCap',
    defaultFormat: 'trade-6x9',
    availableFormats: ['digest-5.5x8.5', 'trade-6x9', 'textbook-7x10'],
    features: { showImages: true, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: true },
  },
  {
    id: 'comic',
    name: 'Comic Book',
    description: 'Sequential art storytelling',
    category: 'books',
    icon: 'MessageSquare',
    defaultFormat: 'comic-standard',
    availableFormats: ['comic-standard', 'manga-digest'],
    features: { showImages: true, showCover: true, showChapters: false, showPageNumbers: true, showHeaders: false, showDataViz: false },
  },
  {
    id: 'manga',
    name: 'Manga',
    description: 'Japanese-style comic',
    category: 'books',
    icon: 'Layers',
    defaultFormat: 'manga-digest',
    availableFormats: ['manga-digest', 'comic-standard'],
    features: { showImages: true, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: false, showDataViz: false },
  },
  {
    id: 'graphic-novel',
    name: 'Graphic Novel',
    description: 'Long-form illustrated narrative',
    category: 'books',
    icon: 'Image',
    defaultFormat: 'comic-standard',
    availableFormats: ['comic-standard', 'letter'],
    features: { showImages: true, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: false, showDataViz: false },
  },
  {
    id: 'poetry-book',
    name: 'Poetry Collection',
    description: 'Collection of poems in book format',
    category: 'books',
    icon: 'Feather',
    defaultFormat: 'pocket-5x8',
    availableFormats: ['pocket-5x8', 'trade-6x9'],
    features: { showImages: false, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: false, showDataViz: false },
  },
  {
    id: 'cookbook',
    name: 'Cookbook',
    description: 'Recipe collection with images',
    category: 'books',
    icon: 'ChefHat',
    defaultFormat: 'picture-8x10',
    availableFormats: ['picture-8x10', 'letter'],
    features: { showImages: true, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: true },
  },
  {
    id: 'art-book',
    name: 'Art Book / Portfolio',
    description: 'Visual showcase of artwork',
    category: 'books',
    icon: 'Palette',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'picture-8x10', 'tabloid'],
    features: { showImages: true, showCover: true, showChapters: false, showPageNumbers: true, showHeaders: false, showDataViz: false },
  },

  // Business
  {
    id: 'report',
    name: 'Business Report',
    description: 'Formal business report with sections',
    category: 'business',
    icon: 'FileText',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'a4'],
    features: { showImages: true, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: true },
  },
  {
    id: 'proposal',
    name: 'Business Proposal',
    description: 'Project or business proposal',
    category: 'business',
    icon: 'FileCheck',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'a4'],
    features: { showImages: true, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: true },
  },
  {
    id: 'handbook',
    name: 'Employee Handbook',
    description: 'Company policies and procedures',
    category: 'business',
    icon: 'Users',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'trade-6x9'],
    features: { showImages: false, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: true },
  },
  {
    id: 'whitepaper',
    name: 'White Paper',
    description: 'Authoritative industry report',
    category: 'business',
    icon: 'FileSearch',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'a4'],
    features: { showImages: true, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: true },
  },
  {
    id: 'presentation',
    name: 'Presentation',
    description: 'Slide deck for meetings',
    category: 'business',
    icon: 'Presentation',
    defaultFormat: 'widescreen',
    availableFormats: ['widescreen', 'standard-4x3'],
    features: { showImages: true, showCover: true, showChapters: false, showPageNumbers: false, showHeaders: false, showDataViz: true },
  },
  {
    id: 'case-study',
    name: 'Case Study',
    description: 'In-depth analysis of a specific project',
    category: 'business',
    icon: 'ClipboardList',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'a4'],
    features: { showImages: true, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: true },
  },

  // Legal
  {
    id: 'demand-letter',
    name: 'Demand Letter',
    description: 'Legal demand for action',
    category: 'legal',
    icon: 'AlertCircle',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'legal'],
    features: { showImages: false, showCover: false, showChapters: false, showPageNumbers: true, showHeaders: true, showDataViz: false },
  },
  {
    id: 'contract',
    name: 'Contract',
    description: 'Legal binding agreement',
    category: 'legal',
    icon: 'FileSignature',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'legal', 'a4'],
    features: { showImages: false, showCover: false, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: true },
  },
  {
    id: 'brief',
    name: 'Legal Brief',
    description: 'Court filing document',
    category: 'legal',
    icon: 'Scale',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'legal'],
    features: { showImages: false, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: true },
  },
  {
    id: 'motion',
    name: 'Motion',
    description: 'Formal request to the court',
    category: 'legal',
    icon: 'Gavel',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'legal'],
    features: { showImages: false, showCover: false, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: false },
  },
  {
    id: 'agreement',
    name: 'Agreement',
    description: 'General legal agreement',
    category: 'legal',
    icon: 'FileCheck',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'a4'],
    features: { showImages: false, showCover: false, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: false },
  },
  {
    id: 'terms-of-service',
    name: 'Terms of Service',
    description: 'Legal terms for services',
    category: 'legal',
    icon: 'ScrollText',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'a4'],
    features: { showImages: false, showCover: false, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: false },
  },

  // Creative
  {
    id: 'screenplay',
    name: 'Screenplay',
    description: 'Film or TV script',
    category: 'creative',
    icon: 'Film',
    defaultFormat: 'letter',
    availableFormats: ['letter'],
    features: { showImages: false, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: false },
  },
  {
    id: 'stageplay',
    name: 'Stage Play',
    description: 'Theater script',
    category: 'creative',
    icon: 'Film',
    defaultFormat: 'letter',
    availableFormats: ['letter'],
    features: { showImages: false, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: false },
  },
  {
    id: 'poetry',
    name: 'Poetry Collection',
    description: 'Collection of poems',
    category: 'creative',
    icon: 'Feather',
    defaultFormat: 'pocket-5x8',
    availableFormats: ['pocket-5x8', 'trade-6x9'],
    features: { showImages: false, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: false, showDataViz: false },
  },
  {
    id: 'short-stories',
    name: 'Short Story Collection',
    description: 'Multiple short stories',
    category: 'creative',
    icon: 'BookMarked',
    defaultFormat: 'digest-5.5x8.5',
    availableFormats: ['digest-5.5x8.5', 'trade-6x9'],
    features: { showImages: false, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: false },
  },
  {
    id: 'zine',
    name: 'Zine',
    description: 'Self-published small magazine',
    category: 'creative',
    icon: 'Newspaper',
    defaultFormat: 'half-letter',
    availableFormats: ['half-letter', 'digest-5.5x8.5'],
    features: { showImages: true, showCover: true, showChapters: false, showPageNumbers: true, showHeaders: false, showDataViz: false },
  },

  // Marketing
  {
    id: 'brochure',
    name: 'Brochure',
    description: 'Marketing tri-fold or pamphlet',
    category: 'marketing',
    icon: 'LayoutPanelLeft',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'a4'],
    features: { showImages: true, showCover: false, showChapters: false, showPageNumbers: false, showHeaders: false, showDataViz: true },
  },
  {
    id: 'newsletter',
    name: 'Newsletter',
    description: 'Periodic publication',
    category: 'marketing',
    icon: 'Mail',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'tabloid'],
    features: { showImages: true, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: true },
  },
  {
    id: 'flyer',
    name: 'Flyer',
    description: 'Single-page promotional material',
    category: 'marketing',
    icon: 'FileImage',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'a4'],
    features: { showImages: true, showCover: false, showChapters: false, showPageNumbers: false, showHeaders: false, showDataViz: false },
  },
  {
    id: 'catalog',
    name: 'Product Catalog',
    description: 'Product listing publication',
    category: 'marketing',
    icon: 'Grid',
    defaultFormat: 'letter',
    availableFormats: ['letter', 'a4'],
    features: { showImages: true, showCover: true, showChapters: true, showPageNumbers: true, showHeaders: true, showDataViz: true },
  },
  {
    id: 'sales-deck',
    name: 'Sales Deck',
    description: 'Sales presentation slides',
    category: 'marketing',
    icon: 'PresentationIcon',
    defaultFormat: 'widescreen',
    availableFormats: ['widescreen', 'standard-4x3'],
    features: { showImages: true, showCover: true, showChapters: false, showPageNumbers: false, showHeaders: false, showDataViz: true },
  },
  {
    id: 'social-post',
    name: 'Social Media Post',
    description: 'Graphics for social platforms',
    category: 'marketing',
    icon: 'Share2',
    defaultFormat: 'social-square',
    availableFormats: ['social-square', 'social-story', 'widescreen'],
    features: { showImages: true, showCover: false, showChapters: false, showPageNumbers: false, showHeaders: false, showDataViz: false },
  },
];

// Helper functions
export function getCategoryById(id: string): CategoryInfo | undefined {
  return CATEGORIES.find(c => c.id === id);
}

export function getDocumentTypeById(id: string): DocumentType | undefined {
  return DOCUMENT_TYPES.find(t => t.id === id);
}

export function getPageFormatById(id: string): PageFormat | undefined {
  return PAGE_FORMATS.find(f => f.id === id);
}

export function getDocumentTypesForCategory(category: string): DocumentType[] {
  return DOCUMENT_TYPES.filter(t => t.category === category);
}

export function getPageFormatsForDocumentType(documentTypeId: string): PageFormat[] {
  const docType = getDocumentTypeById(documentTypeId);
  if (!docType) return [];
  return PAGE_FORMATS.filter(f => docType.availableFormats.includes(f.id));
}
